import React from 'react';
import { Card, Statistic, Progress, Tooltip, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface StatisticsCardProps {
    /** 卡片标题 */
    title: string;
    /** 统计数值 */
    value: number | string;
    /** 数值后缀 */
    suffix?: React.ReactNode;
    /** 数值前缀图标 */
    icon?: React.ReactNode;
    /** 图标颜色 */
    iconColor?: string;
    /** 加载状态 */
    loading?: boolean;
    /** 点击回调 */
    onClick?: () => void;
    /** 数值颜色 */
    valueColor?: string;
    /** 趋势 (正数表示上升，负数表示下降) */
    trend?: number;
    /** 趋势描述文字 */
    trendLabel?: string;
    /** 进度条百分比 (0-100) */
    progress?: number;
    /** 进度条颜色 */
    progressColor?: string;
    /** 底部描述文字 */
    description?: string;
    /** 精确度 (小数位数) */
    precision?: number;
    /** 卡片尺寸 */
    size?: 'default' | 'small';
}

/**
 * 统计卡片组件
 * 用于展示关键指标和统计数据
 */
const StatisticsCard: React.FC<StatisticsCardProps> = ({
    title,
    value,
    suffix,
    icon,
    iconColor,
    loading = false,
    onClick,
    valueColor,
    trend,
    trendLabel,
    progress,
    progressColor,
    description,
    precision = 0,
    size = 'default',
}) => {
    // 渲染趋势指示器
    const renderTrend = () => {
        if (trend === undefined) return null;

        const isPositive = trend > 0;
        const isNegative = trend < 0;
        const trendValue = Math.abs(trend);

        return (
            <Tooltip title={trendLabel || `较上期${isPositive ? '增长' : '下降'} ${trendValue}%`}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginLeft: 8,
                        fontSize: 12,
                        color: isPositive ? '#52c41a' : isNegative ? '#ff4d4f' : '#999',
                    }}
                >
                    {isPositive && <ArrowUpOutlined />}
                    {isNegative && <ArrowDownOutlined />}
                    <span style={{ marginLeft: 2 }}>{trendValue}%</span>
                </span>
            </Tooltip>
        );
    };

    // 渲染进度条
    const renderProgress = () => {
        if (progress === undefined) return null;

        return (
            <Progress
                percent={progress}
                size="small"
                strokeColor={progressColor}
                showInfo={false}
                style={{ marginTop: 8 }}
            />
        );
    };

    // 渲染描述文字
    const renderDescription = () => {
        if (!description) return null;

        return (
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                {description}
            </Text>
        );
    };

    const prefixNode = icon ? (
        <span style={{ color: iconColor, marginRight: 8 }}>{icon}</span>
    ) : undefined;

    return (
        <Card
            hoverable={!!onClick}
            onClick={onClick}
            loading={loading}
            size={size}
            styles={{
                body: {
                    padding: size === 'small' ? 16 : 24,
                },
            }}
        >
            <Statistic
                title={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        {title}
                        {renderTrend()}
                    </span>
                }
                value={value}
                precision={typeof value === 'number' ? precision : undefined}
                valueStyle={{ color: valueColor }}
                prefix={prefixNode}
                suffix={suffix}
            />
            {renderProgress()}
            {renderDescription()}
        </Card>
    );
};

// 预设颜色配置
export const STAT_COLORS = {
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    primary: '#1890ff',
    purple: '#722ed1',
    cyan: '#13c2c2',
};

// 常用图标颜色
export const ICON_COLORS = {
    vehicle: '#52c41a',
    station: '#1890ff',
    dispatch: '#722ed1',
    alert: '#ff4d4f',
    info: '#13c2c2',
};

export default React.memo(StatisticsCard);
